import { useState, useMemo, useCallback } from 'react';

// Client-side text filter across a set of fields.
// Replaces the hand-rolled `filteredX = x.filter(...)` in 19 modules.
//
//   const { searchTerm, setSearchTerm, filtered } = useSearch(events, ['eventId', 'eventName', 'venue']);

/** A field to search: a property name, or a function deriving the text. */
export type SearchField<T> = keyof T | string | ((item: T) => unknown);

export interface UseSearchResult<T> {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filtered: T[];
  clear: () => void;
}

export const useSearch = <T = unknown>(
  items: T[] | undefined,
  fields: Array<SearchField<T>> = [],
  { initialTerm = '' }: { initialTerm?: string } = {}
): UseSearchResult<T> => {
  const [searchTerm, setSearchTerm] = useState<string>(initialTerm);

  const filtered = useMemo<T[]>(() => {
    const list = Array.isArray(items) ? items : [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return list;

    // No fields given -> search every string value on the record.
    const keysFor = (item: T): Array<SearchField<T>> =>
      (fields.length ? fields : (Object.keys(item || {}) as Array<SearchField<T>>));

    return list.filter((item) =>
      keysFor(item).some((field) => {
        const value = typeof field === 'function'
          ? field(item)
          : (item as Record<string, unknown>)?.[field as string];
        return value !== null && value !== undefined && String(value).toLowerCase().includes(term);
      })
    );
  }, [items, fields, searchTerm]);

  const clear = useCallback(() => setSearchTerm(''), []);

  return { searchTerm, setSearchTerm, filtered, clear };
};

export default useSearch;
